/**
 * The report as the drain last saw it (SE-6983).
 *
 * The drain runs OUTSIDE the capture screen — it has to, because a scout who
 * parks work at the ground and regains signal in the car is not looking at the
 * screen when the network comes back. That independence costs one thing: the
 * screen holds its freshest report in component state (`usePlayerEvaluations`'
 * `latestReport`, set from each write's result), and a mutation that resolves
 * somewhere else cannot reach it.
 *
 * Left unaddressed, a successful drain would move the server's counts and not
 * the screen's: "3/50 noted" while five just-synced notes say otherwise. This
 * screen's lineage is full of exactly that failure — a count that describes an
 * older truth than the data behind it — so the drain publishes what it got and
 * the screen reads it.
 *
 * ## Last write wins, and the order is the event order
 *
 * Two sources now produce a fresher report: a local write and a drain. Neither
 * can be judged fresher by inspection — the fragment carries no version — so
 * freshness is tracked by WHEN, not by WHAT: a local write clears this var
 * (that write is newer than any drain before it), and a drain sets it (it is
 * newer than any local write before it). The screen prefers this var when it
 * holds something for the open report, and its own snapshot otherwise.
 *
 * Keyed reads matter: the var holds whatever report the drain last wrote to,
 * which is not necessarily the one on screen — the queue can hold entries for
 * several reports at once. A consumer must check the id before rendering it.
 * @module features/match-report-capture/stores/syncedReport
 */

import { makeVar } from "@apollo/client";

import type { MatchReportCaptureReportFragment } from "@/generated/graphql";

/**
 * The report the drain last wrote to, or null when a local write has
 * superseded it (or nothing has drained this session).
 */
export const syncedReportVar = makeVar<MatchReportCaptureReportFragment | null>(
  null
);

/**
 * Records the report a drained write returned.
 * @param report - The report the server recomputed, or null/undefined to skip.
 */
export const publishSyncedReport = (
  report: MatchReportCaptureReportFragment | null | undefined
): void => {
  if (!report) return;
  syncedReportVar(report);
};

/**
 * Drops the drain's snapshot because a local write is newer.
 *
 * Called on every successful local write rather than only on a matching one:
 * the alternative is comparing ids to decide whether to clear, and a stale
 * snapshot for ANOTHER report is of no use to the screen either — the consumer
 * ignores it. Clearing unconditionally keeps the "newest event wins" rule
 * whole instead of introducing a second one.
 */
export const clearSyncedReport = (): void => {
  syncedReportVar(null);
};
